import { Embroidery, EmbroideryKey, PartialShoesActionDispatch, ShoesState, StateObj } from 'src/types'
import { colorObjs, shadowEdgeColorObjs } from '../../glove/japan/constants/embroidery'
import { useEmbroideryContent } from 'src/hooks/useEmbroideryContent'
import {
  LINE_TYPES,
  OPENING_PATTERNS,
  OUT_SOLES,
  SIZES,
  TOE_GUARD_KINDS,
  TOE_GUARDS,
  TONGUES,
  UPPER_MATERIALS,
  VAMP_STRAP_TYPES
} from '../constants/base'
import { SHOES_DISPATCHER } from '../constants/action'
import {
  ATOMS_LABELS,
  COLORS,
  LINES,
  MAGIC_BELT_BASES,
  MAGIC_BELT_MATERIALS,
  MESH_COLORS,
  STITCHES,
  TOE_GUARD_COLORS,
  TONGUE_LININGS,
  VAMP_STRAPS
} from '../constants/colors'
import { MESH_PARTS, PARTS } from '../constants/parts'
import { EMBROIDERY_POSITIONS, EMBROIDERY_TYPE_FACES } from '../constants/embroidery'
import { embroideryPositionListGenerator, isHalfWidthCharChecker, objectsFilter, embroideryPositionFilter } from 'src/logic'
import { useEmbroideriesDispatcher } from 'src/hooks/useEmbroideriesDispatcher'

export const getShoesObjects = (state: ShoesState) => {
  const { parts } = state
  return {
    parts
  }
}

export const generateShoesDispatcher =
  (dispatch: PartialShoesActionDispatch) =>
  (key: string): ((key: string) => void) => {
    const dispatchBaseStore = {
      // base
      upperMaterial: { options: UPPER_MATERIALS, action: SHOES_DISPATCHER.upperMaterial },
      size: { options: SIZES, action: SHOES_DISPATCHER.size },
      openingPattern: { options: OPENING_PATTERNS, action: SHOES_DISPATCHER.openingPattern },
      vampStrapType: { options: VAMP_STRAP_TYPES, action: SHOES_DISPATCHER.vampStrapType },
      tongue: { options: TONGUES, action: SHOES_DISPATCHER.tongue },
      outSole: { options: OUT_SOLES, action: SHOES_DISPATCHER.outSole },
      lineType: { options: LINE_TYPES, action: SHOES_DISPATCHER.lineType },
      toeGuard: { options: TOE_GUARDS, action: SHOES_DISPATCHER.toeGuard },
      toeGuardKind: { options: TOE_GUARD_KINDS, action: SHOES_DISPATCHER.toeGuardKind },
      // parts
      parts: { options: PARTS, action: SHOES_DISPATCHER.parts },
      // color
      mainUnit: { options: MESH_COLORS, action: SHOES_DISPATCHER.mainUnit },
      tongueFront: { options: MESH_COLORS, action: SHOES_DISPATCHER.tongueFront },
      line: { options: LINES, action: SHOES_DISPATCHER.line },
      line2: { options: LINES, action: SHOES_DISPATCHER.line2 },
      vampStrap: { options: VAMP_STRAPS, action: SHOES_DISPATCHER.vampStrap },
      vampStrapBelt1: { options: VAMP_STRAPS, action: SHOES_DISPATCHER.vampStrapBelt1 },
      vampStrapBelt2: { options: VAMP_STRAPS, action: SHOES_DISPATCHER.vampStrapBelt2 },
      vampStrapBelt3: { options: VAMP_STRAPS, action: SHOES_DISPATCHER.vampStrapBelt3 },
      stitch: { options: STITCHES, action: SHOES_DISPATCHER.stitch },
      tongueLinings: { options: TONGUE_LININGS, action: SHOES_DISPATCHER.tongueLinings },
      middleCutOpening: { options: COLORS['smooth'], action: SHOES_DISPATCHER.middleCutOpening },
      atomsLabel: { options: ATOMS_LABELS, action: SHOES_DISPATCHER.atomsLabel },
      magicBeltMaterial: { options: MAGIC_BELT_MATERIALS, action: SHOES_DISPATCHER.magicBeltMaterial },
      magicBeltBase: { options: MAGIC_BELT_BASES, action: SHOES_DISPATCHER.magicBeltBase },
      toeGuardColor: { options: TOE_GUARD_COLORS, action: SHOES_DISPATCHER.toeGuardColor }
    }
    const item = dispatchBaseStore[key]
    return (selected: string) => {
      dispatch({ type: item.action, [key]: item.options.filter((o) => o.value === selected)[0] })
    }
  }

export const generatePerPartsShoesDispatcher =
  (dispatch: PartialShoesActionDispatch) =>
  (colorOptions: StateObj[]) =>
  (key: string): ((key: string) => void) => {
    const item = { options: colorOptions, action: SHOES_DISPATCHER[key] }
    return (selected: string) => {
      dispatch({ type: item.action, [key]: objectsFilter(selected, item.options) })
    }
  }

export const useAtomsEmbroideriesDispatchGenerator = (dispatch: PartialShoesActionDispatch, embroideries: Embroidery[]) => {
  const atomsEmbroideriesDispatcher = (key: EmbroideryKey) => {
    const atomsObjects = {
      position: EMBROIDERY_POSITIONS,
      typeFace: EMBROIDERY_TYPE_FACES,
      color: colorObjs,
      shadowColor: shadowEdgeColorObjs,
      edgeColor: shadowEdgeColorObjs
    }
    return (selected: string, i?: number) => useEmbroideriesDispatcher(dispatch, embroideries, selected, atomsObjects, key, i)
  }
  const handleContentAndNum = useEmbroideryContent(dispatch, embroideries)
  return {
    handle: {
      typeFace: atomsEmbroideriesDispatcher('typeFace'),
      position: atomsEmbroideriesDispatcher('position'),
      color: atomsEmbroideriesDispatcher('color'),
      shadowColor: atomsEmbroideriesDispatcher('shadowColor'),
      edgeColor: atomsEmbroideriesDispatcher('edgeColor'),
      content: (selected: string, i?: number) => {
        if (i === undefined) return
        handleContentAndNum(selected, i)
      }
    }
  }
}

export const getSizes = (state: ShoesState) => {
  const outSole = state.outSole.value

  switch (outSole) {
    case 'resinSoleEvaWhiteCleats':
    case 'runningShoesSole':
      return SIZES
    case 'pointSole':
    case 'artificialTurfSole':
      return SIZES.filter((size) => !['23.5', '24', '30', '29.5'].includes(size.value))

    case 'leatherSoleWithEva':
      return SIZES.filter((size) => !['23.5', '24'].includes(size.value))
  }
}

export const getOpeningPatterns = (state: ShoesState) => {
  const tongue = state.tongue.value
  return OPENING_PATTERNS.filter((openingPattern) => {
    if (tongue === 'foldingTongue') return openingPattern.value !== 'quarterCut'
    return true
  })
}

export const getTongues = (state: ShoesState) => {
  const openingPattern = state.openingPattern.value
  return TONGUES.filter((tongue) => {
    if (openingPattern === 'quarterCut') return tongue.value === 'standingTongue'
    return true
  })
}

export const getToeGuardKinds = (state: ShoesState) => {
  const outSole = state.outSole.value
  const options = TOE_GUARD_KINDS.filter((option) => {
    if (['runningShoesSole', 'artificialTurfSole', 'pointSole'].includes(outSole)) {
      return !['forPitcherWithMetalReinforcement', 'forFielderWithMetalReinforcement'].includes(option.value)
    }
    return true
  })
  return options
}

export const getEmbroideryPositions = () => {
  return EMBROIDERY_POSITIONS
}

const getContentMaxLength = (position: string, isHalfWidthChar: boolean): number => {
  switch (position) {
    case 'healDecoration':
      return 2
    case 'healCenterLeftFoot':
    case 'healCenterRightFoot':
      return isHalfWidthChar ? 20 : 10
    case 'healSideLeftFoot':
    case 'healSideRightFoot':
      return isHalfWidthChar ? 12 : 6
    default:
      return 0
  }
}

export const characterCheckHelper = (embroidery: Embroidery) => {
  const { content, position } = embroidery
  const isHalfWidthChar = isHalfWidthCharChecker(content)
  const isJapanese = content.charCodeAt(0) >= 256
  const isKoreaContents = content.charCodeAt(0) >= 40001
  const typeFaceObjs = EMBROIDERY_TYPE_FACES
  const typeFaceJaObjs = typeFaceObjs.filter((t) => ['serif'].includes(t.value))
  const typeFaceEnObjs = typeFaceObjs.filter((t) => ['sans-serif', 'Brush Script MT', 'cursive'].includes(t.value))
  const typeFaceObjects = isJapanese ? typeFaceJaObjs : typeFaceEnObjs
  const contentMaxLength = getContentMaxLength(position.value, isHalfWidthChar)
  return {
    contentMaxLength,
    existsContent: content.length > 0,
    typeFaceObjects,
    isKoreaContents,
    contentCaution: isKoreaContents ? '한자,영어만 자수가능합니다. 한글은 자수 불가입니다.' : ''
  }
}

export const getUpperMaterial = (state: ShoesState) => {
  const isMesh = ['enamelMesh', 'nubuckMesh', 'smoothMesh', 'robenicaMesh'].includes(state.upperMaterial.value)
  const isTongueFrontMesh = isMesh && state.tongue.value === 'standingTongue'
  return {
    isMesh,
    isTongueFrontMesh
  }
}

export const getShoesParts = (state: ShoesState) => {
  const { isMesh } = getUpperMaterial(state)
  const isFoldingTongue = state.tongue.value === 'foldingTongue' // 折りベロ
  const isFoldingTongueWithMesh = !isFoldingTongue && isMesh
  // メッシュ & 折ベロじゃなかったら除外したい
  const selectableParts1 = isMesh ? MESH_PARTS : PARTS
  const selectableParts = isFoldingTongueWithMesh ? selectableParts1.filter((part) => part.value !== 'tongueFront') : selectableParts1
  return { selectableParts }
}

export const getSelectableShoesEmbroideryPositions = (embroideries: Embroidery[], positionObjects: StateObj[], index: number): StateObj[] => {
  const selectedPosition = embroideryPositionListGenerator(embroideries, index)
  const selectablePositionBase = embroideryPositionFilter(positionObjects, selectedPosition)
  const overAllSelectedPositions = embroideries.map((e) => e.position.value)

  const selectablePosition = selectablePositionBase.filter((p) => {
    // 既存の制約
    const isNotSelected = !overAllSelectedPositions.includes(p.value) || p.value === embroideries[index].position?.value

    // 右足の制約
    if (overAllSelectedPositions.includes('healSideRightFoot') && p.value === 'healCenterRightFoot') return false
    if (overAllSelectedPositions.includes('healCenterRightFoot') && p.value === 'healSideRightFoot') return false
    // 左足の制約
    if (overAllSelectedPositions.includes('healSideLeftFoot') && p.value === 'healCenterLeftFoot') return false
    if (overAllSelectedPositions.includes('healCenterLeftFoot') && p.value === 'healSideLeftFoot') return false

    return isNotSelected
  })

  return selectablePosition
}

export const getOutSoles = (state: ShoesState) => {
  const { toeGuardKind } = state
  // ソールが以下の場合、金属補強入りのつま先ガードは選択不可なので除外
  const excludeSoleValues = ['runningShoesSole', 'artificialTurfSole', 'pointSole']
  if (['forPitcherWithMetalReinforcement', 'forFielderWithMetalReinforcement'].includes(toeGuardKind.value)) {
    return OUT_SOLES.filter((sole) => !excludeSoleValues.includes(sole.value))
  }
  return OUT_SOLES
}
